import React, { useEffect, useState } from "react";
import { Button, Form, Alert, Badge } from "react-bootstrap";
import { listMyCoupons, createMyCoupon, updateMyCoupon, deleteMyCoupon } from "../services/partnerAuthService";

const getErr = (err, fallback) => err.response?.data?.error || fallback;
const emptyForm = { code: "", discountType: "Percent", value: "", maxDiscount: "", minBookingAmount: "", expiresAt: "", usageLimit: "" };

export default function Offers() {
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  const load = () => {
    setLoading(true);
    listMyCoupons()
      .then(({ data }) => setCoupons(data.data || []))
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  const handleCreate = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.code || !form.value) return setError("Code and value are required");
    setSaving(true);
    try {
      await createMyCoupon({
        code: form.code,
        discountType: form.discountType,
        value: Number(form.value),
        maxDiscount: form.maxDiscount ? Number(form.maxDiscount) : null,
        minBookingAmount: form.minBookingAmount ? Number(form.minBookingAmount) : 0,
        expiresAt: form.expiresAt || null,
        usageLimit: form.usageLimit ? Number(form.usageLimit) : null,
      });
      setForm(emptyForm);
      setShowForm(false);
      load();
    } catch (err) {
      setError(getErr(err, "Failed to create offer"));
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (coupon) => {
    try {
      await updateMyCoupon(coupon._id, { isActive: !coupon.isActive });
      load();
    } catch (err) {
      setError(getErr(err, "Failed to update offer"));
    }
  };

  const remove = async (coupon) => {
    if (!window.confirm("Delete this offer?")) return;
    try {
      await deleteMyCoupon(coupon._id);
      load();
    } catch (err) {
      setError(getErr(err, "Failed to delete offer"));
    }
  };

  if (loading) return <p>Loading offers…</p>;

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h4 className="mb-0">Offers &amp; Coupons</h4>
        <Button size="sm" onClick={() => setShowForm(!showForm)}>{showForm ? "Cancel" : "+ Add Offer"}</Button>
      </div>
      {error && <Alert variant="danger">{error}</Alert>}

      {showForm && (
        <div className="partner-card mb-4">
          <Form onSubmit={handleCreate}>
            <Form.Group className="mb-2">
              <Form.Label>Coupon Code</Form.Label>
              <Form.Control placeholder="e.g. SAVE10" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} />
            </Form.Group>
            <div className="d-flex gap-2">
              <Form.Group className="mb-2 flex-fill">
                <Form.Label>Discount Type</Form.Label>
                <Form.Select value={form.discountType} onChange={(e) => setForm({ ...form, discountType: e.target.value })}>
                  <option value="Percent">Percent (%)</option>
                  <option value="Fixed">Fixed (₹)</option>
                </Form.Select>
              </Form.Group>
              <Form.Group className="mb-2 flex-fill">
                <Form.Label>Value</Form.Label>
                <Form.Control type="number" value={form.value} onChange={(e) => setForm({ ...form, value: e.target.value })} />
              </Form.Group>
            </div>
            {form.discountType === "Percent" && (
              <Form.Group className="mb-2">
                <Form.Label>Max Discount (₹, optional cap)</Form.Label>
                <Form.Control type="number" value={form.maxDiscount} onChange={(e) => setForm({ ...form, maxDiscount: e.target.value })} />
              </Form.Group>
            )}
            <Form.Group className="mb-2">
              <Form.Label>Minimum Booking Amount (₹)</Form.Label>
              <Form.Control type="number" value={form.minBookingAmount} onChange={(e) => setForm({ ...form, minBookingAmount: e.target.value })} />
            </Form.Group>
            <div className="d-flex gap-2">
              <Form.Group className="mb-3 flex-fill">
                <Form.Label>Expires On (optional)</Form.Label>
                <Form.Control type="date" value={form.expiresAt} onChange={(e) => setForm({ ...form, expiresAt: e.target.value })} />
              </Form.Group>
              <Form.Group className="mb-3 flex-fill">
                <Form.Label>Usage Limit (optional)</Form.Label>
                <Form.Control type="number" value={form.usageLimit} onChange={(e) => setForm({ ...form, usageLimit: e.target.value })} />
              </Form.Group>
            </div>
            <Button type="submit" disabled={saving}>{saving ? "Saving…" : "Save Offer"}</Button>
          </Form>
        </div>
      )}

      {coupons.length === 0 ? (
        <div className="partner-card partner-empty">Create an offer to attract more bookings.</div>
      ) : (
        <div className="d-flex flex-column gap-3">
          {coupons.map((c) => (
            <div key={c._id} className="partner-card d-flex justify-content-between align-items-center flex-wrap gap-2">
              <div>
                <h6 className="mb-1">{c.code} <Badge bg={c.isActive ? "success" : "secondary"}>{c.isActive ? "Active" : "Paused"}</Badge></h6>
                <small className="text-muted">
                  {c.discountType === "Percent" ? `${c.value}% off` : `₹${c.value} off`}
                  {c.maxDiscount ? ` (max ₹${c.maxDiscount})` : ""} · min order ₹{c.minBookingAmount || 0}
                  {c.expiresAt && ` · expires ${new Date(c.expiresAt).toLocaleDateString()}`}
                  {` · used ${c.usedCount || 0}${c.usageLimit ? `/${c.usageLimit}` : ""}`}
                </small>
              </div>
              <div className="d-flex gap-2">
                <Button size="sm" variant="outline-secondary" onClick={() => toggleActive(c)}>{c.isActive ? "Pause" : "Resume"}</Button>
                <Button size="sm" variant="outline-danger" onClick={() => remove(c)}>Delete</Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
